/**
 * DemoCTA Component - Call-to-action screen shown to demo tenants when pipeline reaches setup_complete
 *
 * Version History:
 * v4.25 - 2026-10-05 - SCREENS-301: attribution path aligned to the backend route
 *         /api/onboarding/demo-wow-reached (was demo-wow, which d75-agent does not serve); upgrade
 *         checkout POST routed through the authenticated axios client so the Clerk bearer attaches.
 * v3.87 - 2026-06-10 - STORY-10E-C: catalog-driven upgrade tier_id; no 'monthly_499' literal
 * v3.86 - 2026-03-16 - PLG: handleUpgrade now calls Stripe checkout (replaces /register redirect)
 * v3.85 - 2026-03-16 - MKTG-03: Replace mailto:sales@mylojic.com with ContactFormModal
 * v3.61 - 2026-03-06 - DEMO-01: Initial implementation — fire-and-forget wow_reached attribution on mount
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import api, { plansService } from '../services/api';
import ContactFormModal from './ContactFormModal';
import { findUpgradeTier } from '../utils/planCatalog';

const DemoCTA: React.FC = () => {
  // v3.85 - MKTG-03: contact modal state (replaces mailto:sales@mylojic.com)
  const [contactOpen, setContactOpen] = useState(false);

  // v4.25 - SCREENS-301: attribution path aligned to backend (demo-wow -> demo-wow-reached). - 2026-10-05
  useEffect(() => {
    api.post('/onboarding/demo-wow-reached')
      .catch((err) => {
        // Non-blocking — attribution failure must not break user flow
        console.warn('v4.25: demo wow_reached attribution failed (non-blocking):', err);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // v3.87 - STORY-10E-C: catalog-driven upgrade (no literal tier_id)
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const tiers = await plansService.getPlans();
      const upgradeTier = findUpgradeTier(tiers);
      if (!upgradeTier) { setUpgradeLoading(false); return; }
      // v4.25 - SCREENS-301: upgrade checkout POST routed via the authenticated axios client
      //         so the Clerk bearer attaches; raw fetch sent no auth and 401'd. - 2026-10-05
      const res = await api.post<{ checkout_url?: string; checkoutUrl?: string }>(
        '/billing/checkout',
        { plan: upgradeTier.tier_id }
      );
      const data = res.data;
      const url = data?.checkout_url || data?.checkoutUrl;
      if (url) {
        window.location.href = url;
      } else {
        console.error('v4.25: DemoCTA upgrade — no checkout_url in response', data);
      }
    } catch (err) {
      console.error('v4.25: DemoCTA upgrade checkout failed', err);
    } finally {
      setUpgradeLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        px: 3,
        py: 6,
        textAlign: 'center',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 540,
          width: '100%',
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: 3, color: 'primary.main' }}>
          <AutoGraphIcon sx={{ fontSize: 64 }} />
        </Box>

        {/* Headline */}
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          You just saw MyLojic in action.
        </Typography>

        {/* Subhead */}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Your insights were generated from your real data, automatically.
          No manual setup. No templates. Just answers.
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Primary CTA */}
        <Button
          variant="contained"
          size="large"
          startIcon={upgradeLoading ? undefined : <RocketLaunchIcon />}
          onClick={handleUpgrade}
          disabled={upgradeLoading}
          fullWidth
          sx={{ py: 1.5, mb: 2, fontSize: '1rem', fontWeight: 700 }}
        >
          {upgradeLoading ? <CircularProgress size={22} color="inherit" /> : 'Connect More Data & Upgrade'}
        </Button>

        {/* Secondary CTA — v3.85: replaced mailto: with ContactFormModal */}
        <Button
          variant="outlined"
          size="large"
          startIcon={<HeadsetMicIcon />}
          onClick={() => setContactOpen(true)}
          fullWidth
          sx={{ py: 1.5, fontSize: '1rem' }}
        >
          Talk to Sales
        </Button>
        <ContactFormModal
          open={contactOpen}
          onClose={() => setContactOpen(false)}
          title="Talk to Sales"
          initialMessage="Hi, I'd like to learn more about upgrading from the demo."
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          Demo accounts are limited to {3} connections and 1 insight. Upgrade for unlimited access.
        </Typography>
      </Paper>
    </Box>
  );
};

export default DemoCTA;
